import React, { useMemo, useState } from "react";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { KeyboardArrowDownRounded } from "@mui/icons-material";

import Button, { ButtonTypes } from "../../../../../components/Button";
import { DropdownAction } from "../../../../../components/Dropdown/types";
import { Pagination } from "../../../../../components/Pagination";
import { DeletePopUp } from "../../../../../components/PopUp";
import { ConfirmDetail } from "../../../../../components/PopUp/confirmShared/types";
import { SortableColumnHeader } from "../../../../../components/SortableColumnHeader";
import { useResponsive } from "../../../../../hooks/useResponsive";
import {
  ErrorResponse,
  getFieldErrorMessages,
  useGetAccountsYearsQuery,
} from "../../../../../services";
import { ActivityEntry, TradeCommand } from "../../../../../services/holdings";
import { SECTION_CARD_BREAKPOINT } from "../../constants";
import {
  capitalizeFirstLetter,
  formatCurrency,
  SortState,
} from "../../../../../utils";
import { localToday } from "../../../../../utils/dates";
import {
  activityDateCoversYears,
  activityDateRangeText,
} from "../../../activityRequest";
import { CashTransferDeleteText } from "../../../CashTransferDeleteText";
import {
  CASH_TRANSFER_DELETE_TITLE,
  TRANSFER_COUNTERPARTY_FALLBACK,
} from "../../../constants";
import { commandNoun } from "../../../utils";
import { useAccountActivity } from "../../hooks";
import { SectionFreshnessNote } from "../SectionFreshnessNote";
import { REFRESH_NOTICE_COPY, RefreshNotice } from "../RefreshNotice";
import { SectionEmptyState } from "../SectionCard";
import { activityHoldingOptionsOf } from "./activityHoldingOptions";
import { ActivityToolbar } from "./ActivityToolbar";
import {
  activityEntriesText,
  expectedPageRows,
  numberedPageCount,
  numberedPageState,
  numberedPageSummary,
} from "./activityPaging";
import {
  ACTIVITY_COLUMNS,
  ACTIVITY_ORDER_PHRASES,
  EMPTY_RANGE_BODY,
  EMPTY_RANGE_TITLE,
  NEW_ACCOUNT_BODY,
  NEW_ACCOUNT_TITLE,
  NO_MATCH_BODY,
  NO_MATCH_TITLE,
} from "./constants";
import { buildTradeCommandDisplay, chargeNoun } from "./tradeCommandDisplay";
import { CorporateActionCard } from "./CorporateActionCard";
import { CorporateActionRow } from "./CorporateActionRow";
import { TradeCommandCard } from "./TradeCommandCard";
import { TradeCommandRow } from "./TradeCommandRow";
import { ActivityFeedProps, NumberedPageState } from "./types";
import { useActivityRequest } from "./useActivityRequest";
import {
  ActivityFeedCard,
  Badge,
  Body,
  CardList,
  DateRangeNote,
  Footer,
  FooterText,
  HeaderCell,
  HeaderRow,
  HiddenHeaderLabel,
  LoadMoreLabel,
  Message,
  NoticeInset,
  PageNote,
  PagerBand,
  RetryButton,
  Subtitle,
  Table,
  Title,
  TitleBlock,
} from "./styles";

// An empty page beside a cursor means a hydration skip, not an empty ledger — the client resumes from the cursor.
const SKIPPED_PAGE_MESSAGE =
  "We couldn't show this part of the activity. Load more to continue from here.";
// Numbered pages are never backfilled, so a hydration skip is named on the page it hit rather than resumed from.
const SHORT_PAGE_NOTE =
  "Some entries on this page couldn't be shown, so it lists fewer than usual. Other pages are unaffected.";
const UNAVAILABLE_PAGE_MESSAGE =
  "We couldn't show the entries on this page. Other pages are unaffected.";
const PAST_END_MESSAGE = "No more activity on this page.";
const cursorFallbackNote = (orderPhrase: string) =>
  `Numbered pages aren't available this far back, so the activity loads ${orderPhrase} instead.`;
const DELETE_FALLBACK_ERROR = "We couldn't delete this trade.";
const TRANSFER_DELETE_FALLBACK_ERROR = "We couldn't delete this cash transfer.";

// An action id alone isn't unique: one action hitting two positions shares it (D19), so
// the holding must be part of the key or React collapses the two rows into one.
const entryKeyOf = (entry: ActivityEntry) =>
  entry.kind === "command"
    ? `command-${entry.commandId}`
    : `action-${entry.corporateActionId}-${entry.holdingId}`;

const ActivityFeedComponent: React.FC<ActivityFeedProps> = ({
  accountId,
  investmentId,
  investmentName,
  settlementAccountName,
  holdings,
  onEdit,
  onEditTransfer,
  onDeleteTransfer,
  generation,
}) => {
  const isMobile = useResponsive(SECTION_CARD_BREAKPOINT);
  const [pendingDelete, setPendingDelete] = useState<TradeCommand | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const {
    request,
    searchInput,
    setSearchInput,
    updateFilters,
    setPage,
    isCursorMode,
    switchToCursorMode,
    hasActiveFilters,
  } = useActivityRequest(accountId);

  const {
    entries,
    hasNext,
    pagination,
    isLoading: isActivityLoading,
    isLoadingMore,
    error,
    refreshFailed,
    isRefreshing,
    numberedUnavailable,
    freshness,
    loadMore,
    reload,
    removeCommand,
  } = useAccountActivity({
    accountId,
    request,
    skip: !accountId,
    generation,
    settlementAccountName,
  });
  const { data: accountYears } = useGetAccountsYearsQuery(accountId, {
    skip: !accountId,
  });

  // Switched during render, not in an effect, so the refused page never paints an empty feed first.
  if (numberedUnavailable && !isCursorMode) {
    switchToCursorMode();
  }

  const isLoading = isActivityLoading || numberedUnavailable;
  const shownCount = entries.length;
  const hasEntries = shownCount > 0;
  const orderPhrase = ACTIVITY_ORDER_PHRASES[request.order];
  const pageState: NumberedPageState | null =
    pagination && !isLoading ? numberedPageState(pagination, shownCount) : null;

  // An empty page is only proof of an empty ledger when its range spans every year the account has existed.
  const coversAccountLifetime = activityDateCoversYears(
    request.date,
    accountYears ?? [],
  );

  const years = useMemo(() => {
    const sorted = [...(accountYears ?? [])].sort((a, b) => b - a);
    return sorted.length > 0 ? sorted : [localToday().getFullYear()];
  }, [accountYears]);

  const holdingOptions = useMemo(
    () => activityHoldingOptionsOf(holdings),
    [holdings],
  );

  const openDeleteModal = (command: TradeCommand) => {
    // Cleared on open as well as on dismiss: a message left over from the last
    // failed attempt would otherwise greet the next command's confirmation.
    setDeleteError(null);
    setPendingDelete(command);
  };

  // Built from the server's flags, never re-derived from commandOrigin — correctionPolicyFor on the
  // API owns that policy. Transfer branches off first since it has no trade correction of its own.
  const buildRowActions = (command: TradeCommand): DropdownAction[] => {
    if (command.transfer !== null) {
      const { transactionId, counterpartyAccountName } = command.transfer;
      const reference =
        counterpartyAccountName ??
        capitalizeFirstLetter(TRANSFER_COUNTERPARTY_FALLBACK);
      return [
        {
          id: "edit-transfer",
          label: `Edit linked transfer · ${reference}`,
          icon: <EditIcon fontSize="small" />,
          onClick: () => onEditTransfer?.(transactionId),
          show: Boolean(onEditTransfer),
        },
        {
          id: "delete-transfer",
          label: `Delete linked transfer · ${reference}`,
          icon: <DeleteIcon fontSize="small" />,
          onClick: () => openDeleteModal(command),
          variant: "destructive",
          show: Boolean(onDeleteTransfer),
        },
      ];
    }
    const noun = commandNoun(command);
    return [
      {
        id: "edit",
        label: `Edit ${noun}`,
        icon: <EditIcon fontSize="small" />,
        onClick: () => onEdit?.(command),
        show: command.canEdit && Boolean(onEdit),
      },
      {
        id: "delete",
        label: `Delete ${noun}`,
        icon: <DeleteIcon fontSize="small" />,
        onClick: () => openDeleteModal(command),
        variant: "destructive",
        show: command.canDelete,
      },
    ];
  };

  const closeDeleteModal = () => {
    if (isDeleting) {
      return;
    }
    setPendingDelete(null);
    setDeleteError(null);
  };

  // pendingDelete clears only on success: a rejected DELETE (e.g. TradeCorrectionConflictError)
  // needs the dialog open so the error is visible and resubmitting stays possible.
  const handleConfirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    const transfer = pendingDelete.transfer;
    // An invariant backstop: optional-chaining a missing handler would close
    // the dialog as though the transfer had been deleted.
    if (transfer && !onDeleteTransfer) {
      return;
    }
    setIsDeleting(true);
    setDeleteError(null);
    try {
      if (transfer && onDeleteTransfer) {
        await onDeleteTransfer(transfer.transactionId);
      } else {
        await removeCommand(pendingDelete, investmentId);
      }
      setPendingDelete(null);
    } catch (err: unknown) {
      // The global middleware already toasted; this inline copy is what keeps
      // the reason visible next to the button that would retry it.
      const fallback = transfer
        ? TRANSFER_DELETE_FALLBACK_ERROR
        : DELETE_FALLBACK_ERROR;
      // `getFieldErrorMessages`, not `getErrorMessages`: the latter substitutes
      // a generic "Server Error", which would make these fallbacks unreachable.
      const [reason] = getFieldErrorMessages(err as ErrorResponse);
      setDeleteError(reason?.message ?? fallback);
    } finally {
      setIsDeleting(false);
    }
  };

  const dateSort: SortState<"date"> = {
    key: "date",
    dir: request.order === "ASC" ? "asc" : "desc",
  };
  const toggleDateOrder = () => {
    updateFilters({ order: request.order === "ASC" ? "DESC" : "ASC" });
  };

  // The actions column is unlabelled on screen but still a column — naming it keeps that honest for a screen reader.
  const headerCells = ACTIVITY_COLUMNS.map(
    ({ key, label, align, width, labelHidden }) => {
      if (key === "date") {
        return (
          <SortableColumnHeader
            key={label}
            column={key}
            label={label}
            width={width}
            variant="section"
            sort={dateSort}
            onSort={toggleDateOrder}
          />
        );
      }
      const heading = labelHidden ? (
        <HiddenHeaderLabel>{label}</HiddenHeaderLabel>
      ) : (
        label
      );
      return (
        <HeaderCell key={label} scope="col" $align={align} $width={width}>
          {heading}
        </HeaderCell>
      );
    },
  );

  // Server order is rendered verbatim — re-sorting here would disagree with the
  // cursor the next page resumes from.
  const renderCard = (entry: ActivityEntry) => {
    if (entry.kind === "action") {
      return <CorporateActionCard key={entryKeyOf(entry)} entry={entry} />;
    }
    return (
      <TradeCommandCard
        key={entryKeyOf(entry)}
        command={entry}
        actions={buildRowActions(entry)}
        investmentName={investmentName}
        settlementAccountName={settlementAccountName}
      />
    );
  };

  const renderRow = (entry: ActivityEntry) => {
    if (entry.kind === "action") {
      return <CorporateActionRow key={entryKeyOf(entry)} entry={entry} />;
    }
    return (
      <TradeCommandRow
        key={entryKeyOf(entry)}
        command={entry}
        actions={buildRowActions(entry)}
        investmentName={investmentName}
        settlementAccountName={settlementAccountName}
      />
    );
  };

  let emptyState = (
    <SectionEmptyState
      icon={<CalendarTodayOutlinedIcon />}
      title={EMPTY_RANGE_TITLE}
      description={EMPTY_RANGE_BODY}
    />
  );
  if (hasActiveFilters) {
    emptyState = (
      <SectionEmptyState
        icon={<SearchIcon />}
        title={NO_MATCH_TITLE}
        description={NO_MATCH_BODY}
      />
    );
  } else if (coversAccountLifetime) {
    emptyState = (
      <SectionEmptyState
        icon={<ReceiptLongOutlinedIcon />}
        title={NEW_ACCOUNT_TITLE}
        description={NEW_ACCOUNT_BODY}
      />
    );
  }
  let body: React.ReactNode;
  let showsTable = false;
  if (isLoading && !hasEntries) {
    body = <Message>Loading account activity…</Message>;
  } else if (error && !hasEntries) {
    body = (
      <>
        <Message>{error}</Message>
        <RetryButton onClick={() => void reload()} />
      </>
    );
  } else if (pageState === "empty") {
    body = emptyState;
  } else if (pageState === "unavailable") {
    body = <Message>{UNAVAILABLE_PAGE_MESSAGE}</Message>;
  } else if (pageState === "pastEnd") {
    body = <Message>{PAST_END_MESSAGE}</Message>;
  } else if (!hasEntries) {
    body = hasNext ? <Message>{SKIPPED_PAGE_MESSAGE}</Message> : emptyState;
  } else if (isMobile) {
    body = <CardList>{entries.map(renderCard)}</CardList>;
  } else {
    showsTable = true;
    body = (
      <Table aria-label="Account activity">
        <thead>
          <tr>{headerCells}</tr>
        </thead>
        <tbody>{entries.map(renderRow)}</tbody>
      </Table>
    );
  }

  const isShortPage = pageState === "short";
  // Desktop sorts from the Date header, so the select stands in whenever a message replaces the table.
  const showOrderSelect =
    isMobile || (!showsTable && !(isLoading && !hasEntries));

  // Deliberately no "of N" in cursor mode: it returns no total, and inventing one
  // from the rows already loaded would misreport how much history there is.
  const footerText = `Showing ${shownCount} · ${orderPhrase}`;
  // The requested page, not the answered one, so the clicked button is already current while its rows load.
  const currentPage = request.page ?? pagination?.page ?? 1;
  const currentPagination = pagination
    ? { ...pagination, page: currentPage }
    : null;
  const pageCount = currentPagination
    ? numberedPageCount(currentPagination)
    : 0;
  // While the clicked page loads, its range is what the count promises rather than a flash of "Showing 0".
  const summaryRowCount =
    isLoading && currentPagination
      ? expectedPageRows(currentPagination)
      : shownCount;
  const pager = currentPagination ? (
    <PagerBand>
      <Pagination
        page={currentPage - 1}
        pageCount={pageCount}
        onPageChange={(page) => setPage(page + 1)}
        ariaLabel="Activity pages"
        summary={numberedPageSummary(currentPagination, summaryRowCount)}
      />
    </PagerBand>
  ) : null;
  // hasNext alone is enough: a skipped page renders no rows but still has a cursor.
  const showCursorFooter = !pager && (hasEntries || hasNext);
  const showPager = pager !== null && pageState !== "empty";

  // Must not read "0 trades" while loading or after a failure — neither is evidence of an empty ledger.
  let badgeText = pagination
    ? activityEntriesText(pagination.totalRows)
    : `${shownCount} shown`;
  if (isLoading && !hasEntries && !pagination) {
    badgeText = "Loading";
  } else if (error && !hasEntries) {
    badgeText = "Unavailable";
  }

  // Built through the same helper the row rendered from — pill, tone and figure
  // included — so the modal and the row cannot disagree about one command.
  const pendingDisplay = pendingDelete
    ? buildTradeCommandDisplay(
        pendingDelete,
        investmentName,
        settlementAccountName,
      )
    : null;
  const deleteDetails: ConfirmDetail | undefined =
    pendingDelete && pendingDisplay
      ? {
          pill: {
            label: pendingDisplay.typeLabel,
            variant: pendingDisplay.pillVariant,
          },
          title: pendingDisplay.positionText,
          subtitle: pendingDisplay.dateText,
          amount: pendingDisplay.amountText,
          amountVariant: pendingDisplay.amountVariant,
        }
      : undefined;
  const pendingChargeText =
    pendingDelete && pendingDelete.feeEur !== null
      ? `${formatCurrency(pendingDelete.feeEur)} ${chargeNoun(pendingDelete)}`
      : null;

  let deleteTitle = "Delete Trade";
  if (pendingDelete?.transfer) {
    deleteTitle = CASH_TRANSFER_DELETE_TITLE;
  } else if (pendingDelete) {
    deleteTitle = `Delete ${capitalizeFirstLetter(commandNoun(pendingDelete))}`;
  }

  // A settlement/commission or dividend/tax pair is one command, so the copy must say deleting it
  // reverses both rather than let the charge vanish unannounced.
  let deleteText: React.ReactNode = "";
  if (pendingDelete?.transfer) {
    deleteText = (
      <CashTransferDeleteText
        amountEur={pendingDelete.amountEur}
        date={pendingDelete.effectiveDate}
        investmentName={investmentName}
        counterpartyAccountName={pendingDelete.transfer.counterpartyAccountName}
      />
    );
  } else if (pendingDelete) {
    deleteText = (
      <>
        Do you really want to delete the{" "}
        <strong>{pendingDisplay?.typeLabel.toLowerCase()}</strong> of{" "}
        <strong>{pendingDisplay?.amountText}</strong> in{" "}
        <strong>{pendingDisplay?.positionText}</strong>?
        {pendingChargeText && (
          <> That also removes the {pendingChargeText} recorded with it.</>
        )}
      </>
    );
  }

  return (
    <ActivityFeedCard aria-label="Investment account activity">
      <HeaderRow>
        <TitleBlock>
          <Title>Activity</Title>
          <Subtitle>
            Trades, income, costs and transfers in ledger order
          </Subtitle>
          <SectionFreshnessNote freshness={freshness} />
        </TitleBlock>
        <Badge>{badgeText}</Badge>
      </HeaderRow>

      <Body>
        <ActivityToolbar
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          date={request.date}
          onDateChange={(date) => updateFilters({ date })}
          years={years}
          holdingOptions={holdingOptions}
          holdingIds={request.holdingIds}
          accountLevel={request.accountLevel}
          onHoldingsChange={updateFilters}
          types={request.types}
          onTypesChange={(types) => updateFilters({ types })}
          order={request.order}
          onOrderChange={(order) => updateFilters({ order })}
          showOrderSelect={showOrderSelect}
          isCompact={isMobile}
        />
        <DateRangeNote>{activityDateRangeText(request.date)}</DateRangeNote>
        {refreshFailed && (
          <NoticeInset>
            <RefreshNotice
              {...REFRESH_NOTICE_COPY.activity}
              isRetrying={isRefreshing}
              onRetry={() => void reload()}
            />
          </NoticeInset>
        )}
        {body}
        {isShortPage && <PageNote>{SHORT_PAGE_NOTE}</PageNote>}
        {isCursorMode && <PageNote>{cursorFallbackNote(orderPhrase)}</PageNote>}
      </Body>

      {showPager && pager}

      {showCursorFooter && (
        <Footer>
          <FooterText>{footerText}</FooterText>
          {hasNext && (
            <Button
              type={ButtonTypes.primary}
              onClick={() => void loadMore()}
              disabled={isLoadingMore}
            >
              <LoadMoreLabel>
                {isLoadingMore ? "Loading…" : "Load more"}
                <KeyboardArrowDownRounded fontSize="small" />
              </LoadMoreLabel>
            </Button>
          )}
        </Footer>
      )}

      {pendingDelete && (
        <DeletePopUp
          isOpen={Boolean(pendingDelete)}
          setIsOpen={closeDeleteModal}
          title={deleteTitle}
          text={deleteText}
          details={deleteDetails}
          isLoading={isDeleting}
          errorMessage={deleteError}
          handleOnDelete={() => void handleConfirmDelete()}
        />
      )}
    </ActivityFeedCard>
  );
};

export const ActivityFeed = React.memo(ActivityFeedComponent);
